import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import { ToastContainer, toast } from "react-toastify";
import FeeSystemMenu from "./FeeSystemMenu";
import config from "../../Config";
import { scrollToTop } from "../Shared/ScrollOnTop";
import MonthsDropDown from "../Shared/MonthsDropDown";
import YearsDropDown from "../Shared/YearsDropDown";
import ChallanCorrectorCompare from "./ChallanCorrectorCompare";

const MONTH_NAMES = [
  "", "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const ChallanCorrector = () => {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [studentIdInput, setStudentIdInput] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [fixLoading, setFixLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [lastApplied, setLastApplied] = useState(null);

  useEffect(() => {
    scrollToTop();
    document.title = "Challan Corrector";
  }, []);

  const fetchData = useCallback(
    async (studentId, month, year) => {
      const payload = {
        AccessKey,
        UserID,
        MemberType: MemberType || "Admin",
        Student_ID: studentId,
      };
      const m = parseInt(month, 10);
      const y = parseInt(year, 10);
      if (m >= 1 && m <= 12 && y > 1990) {
        payload.SelectedMonth = m;
        payload.FYear = y;
      }

      const res = await axios({
        method: "post",
        url: config.base_url + "FeeSystem/GetChallanCorrectorData",
        headers: { "Content-Type": "application/json" },
        data: payload,
      });
      return res.data;
    },
    [AccessKey, UserID, MemberType]
  );

  const applyResponse = (res) => {
    if (res.status_code !== 1) {
      toast.error(res.status_message || "Could not load data.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      setData(null);
      return false;
    }
    setData(res);
    const initial = {};
    (res.MonthRows || []).forEach((row) => {
      if (row.NeedsCorrection) {
        initial[row.Head_ID] = true;
      }
    });
    setSelected(initial);
    return true;
  };

  const loadData = async (e) => {
    if (e) e.preventDefault();
    setData(null);
    setSelected({});
    setLastApplied(null);

    const sid = parseInt(studentIdInput, 10);
    if (!sid || sid <= 0) {
      toast.error("Please enter a valid student ID.", { position: toast.POSITION.TOP_RIGHT });
      return;
    }

    setSearchLoading(true);
    try {
      const res = await fetchData(sid, filterMonth, filterYear);
      if (applyResponse(res)) {
        if (!filterMonth && (res.AvailableMonths || []).length > 0) {
          const pick =
            (res.MonthRows || []).find((r) => r.NeedsCorrection) ||
            res.AvailableMonths[res.AvailableMonths.length - 1];
          const m = pick.Month;
          const y = pick.Year;
          setFilterMonth(String(m));
          setFilterYear(String(y));
          const filtered = await fetchData(sid, m, y);
          applyResponse(filtered);
        }
        toast.success("Student challan data loaded.", { position: toast.POSITION.TOP_RIGHT });
      }
    } catch {
      toast.error("Network error.", { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setSearchLoading(false);
    }
  };

  const reloadForFilter = async (studentId, month, year) => {
    if (!studentId) return;
    setSearchLoading(true);
    try {
      const res = await fetchData(studentId, month, year);
      applyResponse(res);
    } catch {
      toast.error("Network error.", { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setSearchLoading(false);
    }
  };

  const onFilterChange = (month, year) => {
    if (data?.Student?.Student_ID && month && year) {
      reloadForFilter(data.Student.Student_ID, month, year);
    }
  };

  const toggleRow = (headId) => {
    setSelected((prev) => ({ ...prev, [headId]: !prev[headId] }));
  };

  const toggleAllInvalid = () => {
    if (!data?.MonthRows) return;
    const invalid = data.MonthRows.filter((r) => r.NeedsCorrection);
    const allSelected = invalid.every((r) => selected[r.Head_ID]);
    const next = { ...selected };
    invalid.forEach((r) => {
      next[r.Head_ID] = !allSelected;
    });
    setSelected(next);
  };

  const applyCorrections = async () => {
    if (!data?.Student?.Student_ID) return;

    const corrections = (data.MonthRows || [])
      .filter((row) => selected[row.Head_ID] && row.NeedsCorrection)
      .map((row) => ({
        Month: row.Month,
        Year: row.Year,
        OldChallan_No: row.CurrentChallan_No,
        NewChallan_No: row.ExpectedChallan_No,
      }));

    if (corrections.length === 0) {
      toast.info("Select at least one row that needs correction.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (
      !window.confirm(
        `Update ${corrections.length} challan number(s) in tblChalanHead, tblADLedger, and tblScrollEntry?`
      )
    ) {
      return;
    }

    setFixLoading(true);
    try {
      const res = await axios({
        method: "post",
        url: config.base_url + "FeeSystem/UpdateChallanCorrector",
        headers: { "Content-Type": "application/json" },
        data: {
          AccessKey,
          UserID,
          MemberType: MemberType || "Admin",
          Student_ID: data.Student.Student_ID,
          ChallanCorrections: corrections,
        },
      });

      if (res.data.status_code === 1) {
        setLastApplied({
          at: new Date().toLocaleString(),
          results: res.data.Results || [],
        });
        toast.success(res.data.status_message || "Updated.", {
          position: toast.POSITION.TOP_RIGHT,
        });
        const reload = await fetchData(
          data.Student.Student_ID,
          filterMonth,
          filterYear
        );
        applyResponse(reload);
      } else {
        const failed = (res.data.Results || []).filter((r) => !r.Success);
        toast.error(
          res.data.status_message ||
            (failed.length ? failed.map((f) => f.Message).join("; ") : "Update failed."),
          { position: toast.POSITION.TOP_RIGHT, autoClose: 8000 }
        );
      }
    } catch {
      toast.error("Network error.", { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setFixLoading(false);
    }
  };

  const invalidRows = (data?.MonthRows || []).filter((r) => r.NeedsCorrection);
  const selectedCount = invalidRows.filter((r) => selected[r.Head_ID]).length;

  const filterLabel =
    filterMonth && filterYear
      ? `${MONTH_NAMES[parseInt(filterMonth, 10)]} ${filterYear}`
      : "All months";

  const before = data?.BeforeTables || {};
  const after = data?.AfterTables || {};

  return (
    <>
      <FeeSystemMenu />
      <ToastContainer />
      <div className="page-head">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/Dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/FeeSystem/FeeSystemDashboard">Fee System</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Challan Corrector
                  </li>
                </ol>
              </nav>
              <div className="page-title">Challan Corrector</div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="card mb-3">
            <div className="card-body">
              <p className="text-muted mb-3">
                Review and fix challan numbers per month. Format:{" "}
                <strong>YYMM + 2-digit sequence + Student_ID</strong>. Use the month filter
                below to view tblChalanHead, tblADLedger, and tblScrollEntry before and after
                correction.
              </p>

              <form onSubmit={loadData} className="row g-3 align-items-end">
                <div className="col-md-2">
                  <label className="form-label">Student ID</label>
                  <input
                    type="number"
                    className="form-control"
                    value={studentIdInput}
                    onChange={(e) => setStudentIdInput(e.target.value)}
                    placeholder="e.g. 4425"
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Filter month</label>
                  <select
                    className="form-select"
                    value={filterMonth}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFilterMonth(v);
                      onFilterChange(v, filterYear);
                    }}
                  >
                    <option value="">â€”</option>
                    <MonthsDropDown />
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Filter year</label>
                  <select
                    className="form-select"
                    value={filterYear}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFilterYear(v);
                      onFilterChange(filterMonth, v);
                    }}
                  >
                    <option value="">â€”</option>
                    <YearsDropDown />
                  </select>
                </div>
                <div className="col-md-2">
                  <button type="submit" className="button button-primary w-100" disabled={searchLoading}>
                    {searchLoading ? <Spinner animation="border" size="sm" /> : "Load"}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {data?.Student && (
            <>
              <div className="card mb-3">
                <div className="card-body py-3">
                  <div className="row">
                    <div className="col-md-8">
                      <h5 className="mb-1">{data.Student.Student_Name}</h5>
                      <div className="text-muted small">
                        ID: {data.Student.Student_ID}
                        {data.Student.Registration_No
                          ? ` Â· Reg: ${data.Student.Registration_No}`
                          : ""}
                      </div>
                    </div>
                    <div className="col-md-4 text-md-end">
                      {data.IssueCount > 0 ? (
                        <span className="badge bg-danger">
                          {data.IssueCount} sequence issue(s)
                        </span>
                      ) : (
                        <span className="badge bg-success">All OK</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {invalidRows.length > 0 && (
                <div className="mb-3 d-flex flex-wrap gap-2">
                  <button type="button" className="button button-white" onClick={toggleAllInvalid}>
                    {invalidRows.every((r) => selected[r.Head_ID])
                      ? "Deselect all"
                      : "Select all invalid"}
                  </button>
                  <button
                    type="button"
                    className="button button-primary"
                    disabled={fixLoading || selectedCount === 0}
                    onClick={applyCorrections}
                  >
                    {fixLoading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      `Apply correction (${selectedCount})`
                    )}
                  </button>
                </div>
              )}

              <div className="card mb-3">
                <div className="card-header py-2">
                  <strong>Monthly summary</strong>
                  <span className="text-muted small ms-2">(all months)</span>
                </div>
                <div className="card-body table-responsive p-0">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }} />
                        <th>Month</th>
                        <th>Seq</th>
                        <th>Type</th>
                        <th>Current</th>
                        <th>Expected</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.MonthRows || []).map((row) => (
                        <tr
                          key={row.Head_ID}
                          className={row.NeedsCorrection ? "table-warning" : ""}
                        >
                          <td>
                            {row.NeedsCorrection && (
                              <input
                                type="checkbox"
                                checked={!!selected[row.Head_ID]}
                                onChange={() => toggleRow(row.Head_ID)}
                              />
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-start"
                              onClick={() => {
                                const m = String(row.Month);
                                const y = String(row.Year);
                                setFilterMonth(m);
                                setFilterYear(y);
                                if (data?.Student?.Student_ID) {
                                  reloadForFilter(data.Student.Student_ID, m, y);
                                }
                              }}
                            >
                              {row.MonthLabel}
                            </button>
                          </td>
                          <td>{row.SequenceInMonth}</td>
                          <td>{row.Reference_ID_Type || "â€”"}</td>
                          <td>
                            <code>{row.CurrentChallan_No || "â€”"}</code>
                          </td>
                          <td>
                            <code className={row.NeedsCorrection ? "text-danger" : "text-success"}>
                              {row.ExpectedChallan_No}
                            </code>
                          </td>
                          <td>
                            {row.NeedsCorrection ? (
                              <span className="badge bg-warning text-dark">Fix</span>
                            ) : (
                              <span className="badge bg-success">OK</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {filterMonth && filterYear && (
                <ChallanCorrectorCompare
                  filterLabel={filterLabel}
                  before={before}
                  after={after}
                  searchLoading={searchLoading}
                  lastApplied={lastApplied}
                />
              )}

              {(!filterMonth || !filterYear) && (
                <p className="text-muted">
                  Select a filter month and year (or click a month in the summary table) to view
                  before/after detail for tblChalanHead, tblADLedger, and tblScrollEntry.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ChallanCorrector;
